import React, { useState, useEffect, useContext } from "react";
import { AuthContext, API } from "../App";
import { useNavigate } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { MultiSelect } from "./ui/MultiSelect";
import { Badge } from "./ui/badge";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, FileText } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth } from "date-fns";

export default function ContractorView() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [selectedSites, setSelectedSites] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [attendanceData, setAttendanceData] = useState({});
  const [noShowData, setNoShowData] = useState({}); // Track no-shows separately
  const [personnelTotals, setPersonnelTotals] = useState({}); // company -> total personnel in view
  const [personnelByDate, setPersonnelByDate] = useState({}); // date -> { company -> persons }
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);

  // Color palette for contractors
  const colors = [
    "#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6",
    "#ec4899", "#06b6d4", "#84cc16", "#f97316", "#6366f1",
    "#14b8a6", "#a855f7", "#eab308", "#22c55e", "#fb923c"
  ];

  useEffect(() => {
    loadSites();
    loadCompanies();
  }, []);

  // Default to user's assigned sites
  useEffect(() => {
    if (sites.length > 0 && selectedSites.length === 0 && user) {
      // Filter sites by user's assigned sites
      const userSites = user.role === 'admin' 
        ? sites.map(s => s.id) 
        : sites.filter(s => user.assigned_sites?.includes(s.id)).map(s => s.id);
      setSelectedSites(userSites);
    }
  }, [sites, user]);

  useEffect(() => {
    if (selectedCompanies.length > 0 && selectedSites.length > 0) {
      loadAttendanceData();
    }
  }, [selectedCompanies, selectedSites, currentMonth]);

  const loadSites = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/sites`, {
        headers: { "Authorization": `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSites(data);
      }
    } catch (error) {
      console.error("Error loading sites:", error);
      toast.error("Failed to load sites");
    }
  };

  const loadCompanies = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/companies?active_only=true`, {
        headers: { "Authorization": `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        const contractors = data.filter(c => c.company_type === "subcontractor" || c.company_type === "contractor");
        setCompanies(contractors);
      }
    } catch (error) {
      console.error("Error loading companies:", error);
      toast.error("Failed to load companies");
    }
  };

  const loadAttendanceData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      
      // Get 3 months: previous, current, next
      const startDate = startOfMonth(subMonths(currentMonth, 1));
      const endDate = endOfMonth(addMonths(currentMonth, 1));
      
      const startDateStr = format(startDate, "yyyy-MM-dd");
      const endDateStr = format(endDate, "yyyy-MM-dd");
      
      // Process attendance for all selected sites
      const attendance = {};
      const noShows = {}; // Track no-shows
      const personnel = {}; // company -> total personnel across the visible period
      const byDate = {}; // date -> { company -> persons }
      
      // Fetch daily records for each selected site
      for (const siteId of selectedSites) {
        const response = await fetch(
          `${API}/daily-records?site_id=${siteId}&start_date=${startDateStr}&end_date=${endDateStr}`,
          { headers: { "Authorization": `Bearer ${token}` } }
        );

        if (response.ok) {
          const records = await response.json();
          
          // Process records to find contractor attendance and no-shows
          records.forEach(record => {
            const date = record.date;
            
            // Process actual attendance (subcontractors)
            const subcontractors = record.subcontractors || [];
            subcontractors.forEach(sc => {
              if (selectedCompanies.includes(sc.company)) {
                if (!attendance[date]) {
                  attendance[date] = [];
                }
                if (!attendance[date].includes(sc.company)) {
                  attendance[date].push(sc.company);
                }
                personnel[sc.company] = (personnel[sc.company] || 0) + (sc.number_of_persons || 0);
                if (!byDate[date]) byDate[date] = {};
                byDate[date][sc.company] = (byDate[date][sc.company] || 0) + (sc.number_of_persons || 0);
              }
            });
            
            // Process day labour (now tracked by company)
            const dayLabour = record.day_labour || [];
            dayLabour.forEach(dl => {
              const company = dl.company || dl.trade; // Check company field first, fallback to trade for backwards compatibility
              if (company && selectedCompanies.includes(company)) {
                if (!attendance[date]) {
                  attendance[date] = [];
                }
                if (!attendance[date].includes(company)) {
                  attendance[date].push(company);
                }
                personnel[company] = (personnel[company] || 0) + (dl.number_of || dl.number_of_persons || 0);
                if (!byDate[date]) byDate[date] = {};
                byDate[date][company] = (byDate[date][company] || 0) + (dl.number_of || dl.number_of_persons || 0);
              }
            });
            
            // Process no-shows
            const recordNoShows = record.no_shows || [];
            recordNoShows.forEach(ns => {
              if (selectedCompanies.includes(ns.company)) {
                if (!noShows[date]) {
                  noShows[date] = [];
                }
                if (!noShows[date].includes(ns.company)) {
                  noShows[date].push(ns.company);
                }
              }
            });
          });
        }
      }
      
      setAttendanceData(attendance);
      setNoShowData(noShows);
      setPersonnelTotals(personnel);
      setPersonnelByDate(byDate);
    } catch (error) {
      console.error("Error loading attendance:", error);
      toast.error("Failed to load attendance data");
    } finally {
      setLoading(false);
    }
  };

  const getMonths = () => {
    return [
      subMonths(currentMonth, 1),
      currentMonth,
      addMonths(currentMonth, 1)
    ];
  };

  const getDaysInMonth = (month) => {
    const start = startOfMonth(month);
    const end = endOfMonth(month);
    return eachDayOfInterval({ start, end });
  };

  const getColorForCompany = (company) => {
    const index = selectedCompanies.indexOf(company);
    return colors[index % colors.length];
  };

  const navigateMonth = (direction) => {
    if (direction === 'prev') {
      setCurrentMonth(subMonths(currentMonth, 1));
    } else {
      setCurrentMonth(addMonths(currentMonth, 1));
    }
  };

  const handlePrintReport = async () => {
    if (selectedSites.length === 0 || selectedCompanies.length === 0) {
      toast.error("Select at least one site and one contractor");
      return;
    }
    setPrinting(true);
    try {
      const token = localStorage.getItem("token");
      const startDateStr = format(startOfMonth(subMonths(currentMonth, 1)), "yyyy-MM-dd");
      const endDateStr = format(endOfMonth(addMonths(currentMonth, 1)), "yyyy-MM-dd");
      const res = await fetch(`${API}/contractor-view/report-pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          site_ids: selectedSites,
          company_names: selectedCompanies,
          start_date: startDateStr,
          end_date: endDateStr,
          generated_at: new Date().toLocaleString("en-AU", {
            day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
          }),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to generate report");
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `contractor-attendance-${startDateStr}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (e) {
      console.error("Error generating report:", e);
      toast.error("Failed to generate report");
    } finally {
      setPrinting(false);
    }
  };

  const handleDayClick = (date) => {
    // Navigate to Dashboard with the selected date and site (timezone-safe)
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    // Pass the first selected site if available
    const siteId = selectedSites.length > 0 ? selectedSites[0] : null;
    navigate("/dashboard", { state: { selectedDate: dateStr, siteId: siteId } });
  };

  return (
    <div className="p-4 md:p-6 max-w-full mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-2">
          <CalendarIcon className="w-8 h-8" />
          Contractor View
        </h1>
        <p className="text-slate-600 mt-1">
          View contractor attendance across sites
        </p>
      </div>

      {/* Site Selection */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Select Sites</CardTitle>
        </CardHeader>
        <CardContent>
          <MultiSelect
            options={sites.map(s => ({ value: s.id, label: s.name }))}
            selected={selectedSites}
            onChange={setSelectedSites}
            placeholder="Select sites..."
          />
        </CardContent>
      </Card>

      {/* Contractor Selection */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Select Contractors</CardTitle>
        </CardHeader>
        <CardContent>
          <MultiSelect
            options={companies.map(c => ({ value: c.name, label: c.name }))}
            selected={selectedCompanies}
            onChange={setSelectedCompanies}
            placeholder="Select contractors..."
          />
          
          {/* Legend */}
          {selectedCompanies.length > 0 && (
            <div className="mt-4 flex flex-col gap-2">
              {selectedCompanies.map((company, index) => (
                <div key={company} className="flex items-center gap-3" data-testid={`contractor-legend-${index}`}>
                  <Badge
                    style={{ backgroundColor: getColorForCompany(company) }}
                    className="text-white"
                  >
                    {company}
                  </Badge>
                  <span
                    className="text-sm text-slate-600"
                    data-testid={`contractor-personnel-${index}`}
                    title="Total personnel logged for this contractor across the months shown"
                  >
                    <span className="font-semibold text-slate-800">
                      {personnelTotals[company] || 0}
                    </span>{" "}
                    personnel
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Report button */}
      {selectedSites.length > 0 && selectedCompanies.length > 0 && (
        <div className="flex justify-end mb-3">
          <Button
            onClick={handlePrintReport}
            disabled={printing}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="contractor-view-report-btn"
          >
            <FileText className="w-4 h-4 mr-2" />
            {printing ? "Generating..." : "Print Attendance Report"}
          </Button>
        </div>
      )}

      {/* Calendar Navigation */}
      <div className="flex items-center justify-between mb-4">
        <Button onClick={() => navigateMonth('prev')} variant="outline" size="sm">
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <h2 className="text-xl font-semibold">
          {format(subMonths(currentMonth, 1), "MMMM yyyy")} - {format(addMonths(currentMonth, 1), "MMMM yyyy")}
        </h2>
        <Button onClick={() => navigateMonth('next')} variant="outline" size="sm">
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {selectedSites.length === 0 || selectedCompanies.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-slate-500">
            <p>Please select at least one site and one contractor to view attendance</p>
          </CardContent>
        </Card>
      ) : loading ? (
        <Card>
          <CardContent className="p-8 text-center text-slate-500">
            <p>Loading attendance data...</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {getMonths().map((month, monthIndex) => (
            <Card key={monthIndex}>
              <CardHeader className="bg-slate-700 text-white p-3">
                <CardTitle className="text-center text-lg">
                  {format(month, "MMMM yyyy")}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-2">
                {/* Day headers */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
                    <div key={day} className="text-center text-xs font-semibold text-slate-600 p-1">
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar grid */}
                <div className="grid grid-cols-7 gap-1">
                  {/* Empty cells for days before month starts */}
                  {Array.from({ length: getDaysInMonth(month)[0].getDay() }).map((_, i) => (
                    <div key={`empty-${i}`} className="aspect-square"></div>
                  ))}
                  
                  {/* Days of month */}
                  {getDaysInMonth(month).map(day => {
                    const dateStr = format(day, "yyyy-MM-dd");
                    const contractors = attendanceData[dateStr] || [];
                    const noShowContractors = noShowData[dateStr] || [];
                    const isToday = format(day, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd");

                    // Sort contractors to maintain consistent position across days
                    const sortedContractors = selectedCompanies.map(company => {
                      return contractors.includes(company) ? company : null;
                    }).filter(Boolean);

                    return (
                      <button
                        key={dateStr}
                        onClick={() => handleDayClick(day)}
                        className={`border rounded p-1 text-xs min-h-[60px] cursor-pointer transition-all hover:shadow-md hover:border-blue-400 ${
                          isToday ? "border-blue-500 border-2 bg-blue-50" : "border-slate-200"
                        }`}
                        title="Click to view daily record"
                      >
                        <div className="font-semibold text-center mb-1">{format(day, "d")}</div>
                        <div className="space-y-0.5">
                          {selectedCompanies.map(company => {
                            const isPresent = contractors.includes(company);
                            const isNoShow = noShowContractors.includes(company);
                            const showBlock = isPresent || isNoShow;
                            const count = personnelByDate[dateStr]?.[company] || 0;

                            return (
                              <div
                                key={company}
                                className={`flex items-center gap-1 h-3 ${showBlock ? "" : "opacity-0"}`}
                                title={isNoShow ? `${company} (NO SHOW)` : (isPresent ? `${company}: ${count} personnel` : "")}
                              >
                                <span
                                  className={`h-2.5 w-1.5 rounded-sm flex-shrink-0 ${isNoShow ? "ring-1 ring-red-600" : ""}`}
                                  style={{ backgroundColor: showBlock ? getColorForCompany(company) : "transparent" }}
                                ></span>
                                <span className="text-[9px] leading-none font-semibold text-slate-700">
                                  {isNoShow ? "NS" : (isPresent ? count : "")}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
